import React, { useState } from 'react';
import { colors } from './TabNavigation';
import useIsMobile from './useIsMobile';

const API = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL.trim() : 'http://localhost:5001';

function BirthChartForm({
  onSubmit,
  title = 'Your Birth Chart',
  subtitle = 'Enter the details of your birth to reveal the cosmic map of your soul',
  intro = "We'll calculate your planets, houses, and aspects — and explain each one in plain English.",
  submitLabel = 'Calculate Chart',
  showName = false,
}) {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    day: '',
    month: '',
    year: '',
    hour: '',
    minute: '',
    city: '',
    houseSystem: 'placidus'
  });

  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [unknownTime, setUnknownTime] = useState(false);

  // City geocoding confirmation: 'idle' | 'searching' | 'found' | 'notfound'
  const [geoStatus, setGeoStatus] = useState('idle');
  const [geoResult, setGeoResult] = useState(null);

  const yearMax = new Date().getFullYear();

  // Live, derived per-field validation — runs every render, no extra state needed.
  const fieldError = (name) => {
    const v = formData[name];
    if (v === '') return '';
    const n = Number(v);
    if (name === 'day' && (n < 1 || n > 31)) return 'Day must be between 1 and 31';
    if (name === 'month' && (n < 1 || n > 12)) return 'Month must be between 1 and 12';
    if (name === 'year' && (n < 1900 || n > yearMax)) return `Year must be between 1900 and ${yearMax}`;
    if (name === 'hour' && (n < 0 || n > 23)) return 'Hour must be between 0 and 23';
    if (name === 'minute' && (n < 0 || n > 59)) return 'Minute must be between 0 and 59';
    return '';
  };

  const dateError = fieldError('day') || fieldError('month') || fieldError('year');
  const timeError = fieldError('hour') || fieldError('minute');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    // Any edit to the city invalidates a previous lookup.
    if (name === 'city') {
      setGeoStatus('idle');
      setGeoResult(null);
    }
    if (name === 'day' && value.length === 2) {
      document.querySelector('input[name="month"]')?.focus();
    } else if (name === 'month' && value.length === 2) {
      document.querySelector('input[name="year"]')?.focus();
    } else if (name === 'year' && value.length === 4) {
      document.querySelector('input[name="hour"]')?.focus();
    } else if (name === 'hour' && value.length === 2) {
      document.querySelector('input[name="minute"]')?.focus();
    } else if (name === 'minute' && value.length === 2) {
      document.querySelector('input[name="city"]')?.focus();
    }
  };

  // Confirm the city back to the user the moment they leave the field.
  const handleCityBlur = async () => {
    const city = formData.city.trim();
    if (!city) {
      setGeoStatus('idle');
      setGeoResult(null);
      return;
    }
    setGeoStatus('searching');
    try {
      const response = await fetch(`${API}/api/geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cityName: city }),
      });
      if (!response.ok) throw new Error('not found');
      const data = await response.json();
      if (data && data.latitude && data.displayName) {
        setGeoResult(data);
        setGeoStatus('found');
      } else {
        throw new Error('not found');
      }
    } catch {
      setGeoResult(null);
      setGeoStatus('notfound');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Specific, friendly guidance rather than one catch-all message.
    if (!formData.day || !formData.month || !formData.year) {
      setError('Please enter your full date of birth.');
      return;
    }
    if (dateError) {
      setError(dateError);
      return;
    }
    if (!unknownTime && (!formData.hour || !formData.minute)) {
      setError("Please enter your birth time — or tap “I don't know my birth time”.");
      return;
    }
    if (timeError) {
      setError(timeError);
      return;
    }
    if (!formData.city.trim()) {
      setError('Please enter your birth city.');
      return;
    }
    if (geoStatus === 'notfound') {
      setError("We couldn't find that city. Try adding the country — e.g. “Stockholm, Sweden”.");
      return;
    }

    const date = `${formData.year}-${String(formData.month).padStart(2, '0')}-${String(formData.day).padStart(2, '0')}`;
    const time = `${String(formData.hour).padStart(2, '0')}:${String(formData.minute).padStart(2, '0')}`;
    // Submit the city name (not resolved coords) — the backend re-geocodes so it can
    // compute the DST-correct timezone offset for the birth date. Passing coords alone
    // would default the chart to UTC and shift the Rising sign and houses.
    onSubmit({ date, time, cityName: formData.city, houseSystem: formData.houseSystem, unknownTime, name: formData.name.trim() || null });
  };

  const inputStyle = {
    fontSize: '16px',
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontWeight: '300',
    padding: '14px 0',
    border: 'none',
    borderBottom: `1px solid ${colors.borderSubtle}`,
    backgroundColor: 'transparent',
    outline: 'none',
    color: colors.textPrimary,
    width: '100%',
    transition: 'border-color 0.3s ease',
    appearance: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: isMobile ? '12px' : '10px',
    fontFamily: 'inherit',
    fontWeight: '500',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: '10px',
  };

  const hintStyle = {
    fontSize: isMobile ? '14px' : '12px',
    fontStyle: 'italic',
    color: colors.textMuted,
    marginTop: '8px',
    fontWeight: '300',
  };

  const errorTextStyle = {
    fontSize: isMobile ? '14px' : '12px',
    color: '#c06868',
    marginTop: '8px',
    fontWeight: '300',
  };

  // Date/time field borders turn red once a value is out of range.
  const fieldBorder = (name) =>
    fieldError(name) ? '#c97070' : colors.borderSubtle;

  return (
    <div style={{
      maxWidth: '520px',
      margin: '0 auto',
      padding: '0 24px 80px',
    }}>
      <h1 style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontSize: '34px',
        fontWeight: '400',
        color: colors.textPrimary,
        textAlign: 'center',
        marginBottom: '12px',
        letterSpacing: '-0.3px',
        lineHeight: '1.2',
      }}>
        {title}
      </h1>
      <p style={{
        fontSize: isMobile ? '17px' : '15px',
        fontStyle: 'italic',
        fontWeight: '300',
        color: colors.textSecondary,
        textAlign: 'center',
        marginBottom: '16px',
        lineHeight: '1.7',
      }}>
        {subtitle}
      </p>
      <p style={{
        fontSize: isMobile ? '15px' : '13px',
        fontWeight: '300',
        color: colors.textMuted,
        textAlign: 'center',
        marginBottom: '48px',
        lineHeight: '1.6',
      }}>
        {intro}
      </p>

      {error && (
        <div style={{
          backgroundColor: 'rgba(180, 60, 60, 0.08)',
          border: '1px solid rgba(180, 60, 60, 0.2)',
          color: '#e08080',
          padding: '14px 20px',
          fontSize: isMobile ? '15px' : '13px',
          fontWeight: '300',
          marginBottom: '32px',
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>

        {/* Optional name for person 2 */}
        {showName && (
          <div>
            <label style={labelStyle}>Their name <span style={{ fontWeight: '300', letterSpacing: '0', textTransform: 'none', fontSize: isMobile ? '13px' : '11px' }}>(optional)</span></label>
            <input
              type="text"
              name="name"
              placeholder="e.g. Alex"
              value={formData.name}
              onChange={handleChange}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
              onBlur={(e) => e.target.style.borderBottomColor = colors.borderSubtle}
            />
          </div>
        )}

        {/* Date */}
        <div>
          <label style={labelStyle}>Date of Birth</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '16px' }}>
            {[
              { name: 'day', placeholder: 'DD', min: 1, max: 31 },
              { name: 'month', placeholder: 'MM', min: 1, max: 12 },
              { name: 'year', placeholder: 'YYYY', min: 1900, max: new Date().getFullYear() },
            ].map(({ name, placeholder, min, max }) => (
              <input
                key={name}
                type="number"
                name={name}
                placeholder={placeholder}
                value={formData[name]}
                onChange={handleChange}
                min={min}
                max={max}
                style={{ ...inputStyle, borderBottom: `1px solid ${fieldBorder(name)}` }}
                onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
                onBlur={(e) => e.target.style.borderBottomColor = fieldBorder(name)}
              />
            ))}
          </div>
          {dateError && <p style={errorTextStyle}>{dateError}</p>}
        </div>

        {/* Time */}
        <div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ ...labelStyle, marginBottom: '8px' }}>Time of Birth</label>
            {!unknownTime && (
              <button
                type="button"
                onClick={() => {
                  setUnknownTime(true);
                  setFormData(prev => ({ ...prev, hour: '12', minute: '00' }));
                }}
                style={{
                  display: 'inline-block',
                  background: 'rgba(201, 149, 110, 0.08)',
                  border: `1px solid ${colors.borderGold}`,
                  borderRadius: '2px',
                  fontSize: isMobile ? '13px' : '11px',
                  fontWeight: '500',
                  letterSpacing: '1px',
                  color: colors.gold,
                  cursor: 'pointer',
                  padding: '6px 12px',
                  fontFamily: 'inherit',
                  marginBottom: '12px',
                }}
              >
                I don't know my birth time
              </button>
            )}
          </div>
          {unknownTime ? (
            <div style={{
              padding: '14px 16px',
              backgroundColor: 'rgba(201, 149, 110, 0.06)',
              border: `1px solid ${colors.borderSubtle}`,
              fontSize: isMobile ? '15px' : '13px',
              fontStyle: 'italic',
              fontWeight: '300',
              color: colors.textSecondary,
              lineHeight: '1.6',
            }}>
              We'll use noon as a default. Your Sun and Moon signs will be accurate — your Rising sign and houses won't be.{' '}
              <button
                type="button"
                onClick={() => {
                  setUnknownTime(false);
                  setFormData(prev => ({ ...prev, hour: '', minute: '' }));
                }}
                style={{ background: 'none', border: 'none', color: colors.gold, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: isMobile ? '15px' : '13px', fontStyle: 'italic' }}
              >
                Enter time instead
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {[
                  { name: 'hour', placeholder: 'HH', min: 0, max: 23 },
                  { name: 'minute', placeholder: 'MM', min: 0, max: 59 },
                ].map(({ name, placeholder, min, max }) => (
                  <input
                    key={name}
                    type="number"
                    name={name}
                    placeholder={placeholder}
                    value={formData[name]}
                    onChange={handleChange}
                    min={min}
                    max={max}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
                    onBlur={(e) => e.target.style.borderBottomColor = colors.borderSubtle}
                  />
                ))}
              </div>
              {timeError
                ? <p style={errorTextStyle}>{timeError}</p>
                : <p style={hintStyle}>24-hour format — e.g. 14:30 for 2:30 PM. Your exact time sets your Rising sign and houses, so even 20 minutes can matter.</p>}
            </>
          )}
        </div>

        {/* City */}
        <div>
          <label style={labelStyle}>Birth City</label>
          <input
            type="text"
            name="city"
            placeholder="Stockholm, Sweden"
            value={formData.city}
            onChange={handleChange}
            onBlur={handleCityBlur}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
          />
          {geoStatus === 'searching' && (
            <p style={hintStyle}>Looking up that city…</p>
          )}
          {geoStatus === 'found' && geoResult && (
            <p style={{ ...hintStyle, color: colors.gold, fontStyle: 'normal' }}>
              ✓ Found: {geoResult.displayName}
            </p>
          )}
          {geoStatus === 'notfound' && (
            <p style={errorTextStyle}>
              Couldn't find that city. Try adding the country — e.g. “Stockholm, Sweden”.
            </p>
          )}
          {geoStatus === 'idle' && (
            <p style={hintStyle}>Include the country for best accuracy</p>
          )}
        </div>

        {/* Advanced toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(prev => !prev)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: isMobile ? '14px' : '12px',
              fontStyle: 'italic',
              color: colors.textMuted,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.3px',
            }}
          >
            {showAdvanced ? '▲ Hide advanced options' : '▼ Advanced options'}
          </button>

          {showAdvanced && (
            <div style={{ marginTop: '24px' }}>
              <label style={labelStyle}>House System</label>
              <select
                name="houseSystem"
                value={formData.houseSystem}
                onChange={handleChange}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23c9956e' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 4px center',
                  backgroundSize: '16px',
                  paddingRight: '28px',
                }}
                onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
                onBlur={(e) => e.target.style.borderBottomColor = colors.borderSubtle}
              >
                <option value="placidus" style={{ backgroundColor: '#0a0b0f' }}>Placidus</option>
                <option value="koch" style={{ backgroundColor: '#0a0b0f' }}>Koch</option>
                <option value="whole-sign" style={{ backgroundColor: '#0a0b0f' }}>Whole Sign</option>
                <option value="equal" style={{ backgroundColor: '#0a0b0f' }}>Equal</option>
              </select>
              <p style={hintStyle}>This changes how your chart divides its houses. Cuspli's readings are designed around Placidus — the default, and the system most astrologers use. The other options are here if you specifically prefer one; if you're not sure, leave it on Placidus.</p>
            </div>
          )}
        </div>

        <button
          type="submit"
          style={{
            marginTop: '16px',
            padding: '16px',
            fontSize: isMobile ? '13px' : '11px',
            fontFamily: 'inherit',
            fontWeight: '500',
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: colors.darkBg,
            backgroundColor: colors.gold,
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.goldBright}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.gold}
        >
          {submitLabel}
        </button>

      </form>
    </div>
  );
}

export default BirthChartForm;
